import React, { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Fab, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PaletteIcon from '@mui/icons-material/Palette';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import { ThemeName } from '../../theme/themeConfig';
import { getPhotoUrl } from '../../utils/formatting';
import { device } from '../../theme/classic/useDevice';
import bgLight from '../../theme/classic/bg-light.jpg';
import { usePeEmbed } from '../embedContext';
import { PE_SEARCH_PATH } from '../utils/searchState';
import ProjectAnnouncementsDialog from './ProjectAnnouncementsDialog';

type LoginUser = { Name?: string; Photo?: string } | null;

interface Props {
  children: ReactNode;
  loginUser?: LoginUser;
}

const THEME_ICONS: Record<ThemeName, JSX.Element> = {
  dark: <DarkModeIcon fontSize="small" />,
  light: <LightModeIcon fontSize="small" />,
  classic: <AutoAwesomeIcon fontSize="small" />,
};

const THEME_LABELS: Record<ThemeName, string> = {
  dark: 'Dark',
  light: 'Light',
  classic: 'Classic',
};

export default function ProjectEssentialLayout({ children, loginUser }: Props) {
  const navigate = useNavigate();
  const isEmbed = usePeEmbed();
  const { currentTheme, setTheme } = useAppTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [announcementsOpen, setAnnouncementsOpen] = React.useState(false);
  const userPhotoUrl = getPhotoUrl(loginUser?.Photo);
  const isClassic = currentTheme === 'classic';
  const isWebView = device.isWebView;
  const hasSafeArea = device.hasSafeArea;

  // Apply safe-area padding hint to root when running inside a webview that exposes env(safe-area-inset-*).
  useEffect(() => {
    document.documentElement.dataset.deviceWebview = isWebView ? '1' : '0';
    return () => {
      delete document.documentElement.dataset.deviceWebview;
    };
  }, [isWebView]);

  const handleThemePick = (theme: ThemeName) => {
    setTheme(theme);
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        minHeight: isEmbed ? '100%' : '100vh',
        position: 'relative',
        bgcolor: 'var(--color-bg)',
        color: 'var(--color-text)',
        ...(isClassic
          ? {
              backgroundImage: `url(${bgLight})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              backgroundAttachment: isEmbed ? 'scroll' : 'fixed',
            }
          : {}),
        pt: hasSafeArea ? 'env(safe-area-inset-top)' : 0,
        pb: hasSafeArea ? 'env(safe-area-inset-bottom)' : 0,
      }}
    >
      {/* Header — hidden when PE is embedded (e.g. Super Picker Marketing Materials). */}
      {!isEmbed && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.25,
            bgcolor: isClassic ? 'rgba(255,255,255,0.55)' : 'var(--color-surface)',
            backdropFilter: isClassic ? 'blur(10px)' : 'none',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/project-essential')}
            aria-label="Go to Project Essentials dashboard"
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              borderRadius: 1.25,
              transition: 'background-color 150ms ease, color 150ms ease',
              '&:hover': {
                bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
              },
              '&:hover .pe-brand-title': {
                color: 'var(--color-accent-strong)',
              },
              '&:focus-visible': {
                outline: '2px solid var(--color-accent)',
                outlineOffset: 2,
              },
            }}
          >
            <HomeRoundedIcon
              sx={{
                color: 'var(--color-text-strong)',
                fontSize: { xs: '1.25rem', sm: '1.4rem' },
              }}
            />
            <Typography
              component="span"
              variant="h6"
              className="pe-brand-title"
              sx={{
                fontWeight: 700,
                color: 'var(--color-text-strong)',
                fontSize: { xs: '1rem', sm: '1.15rem' },
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transition: 'color 150ms ease',
              }}
            >
              Project Essentials
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Always-available entry point to universal search, so
                "search everything" never depends on which page's search
                bar the user happens to be looking at. */}
            <IconButton
              onClick={() => navigate(PE_SEARCH_PATH)}
              aria-label="Search Project Essentials"
              sx={{ color: 'var(--color-text-strong)' }}
            >
              <SearchRoundedIcon />
            </IconButton>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label="Change theme"
              sx={{ color: 'var(--color-text-strong)' }}
            >
              <PaletteIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {(['classic', 'light', 'dark'] as ThemeName[]).map((t) => (
                <MenuItem
                  key={t}
                  selected={t === currentTheme}
                  onClick={() => handleThemePick(t)}
                  sx={{ gap: 1, minWidth: 140 }}
                >
                  {THEME_ICONS[t]}
                  <span>{THEME_LABELS[t]}</span>
                </MenuItem>
              ))}
            </Menu>
            {loginUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                <Avatar
                  src={userPhotoUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'rgba(var(--color-text-rgb), 0.15)',
                    fontSize: '0.8rem',
                    color: 'var(--color-text-strong)',
                  }}
                >
                  {(loginUser.Name || 'U')
                    .split(' ')
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'var(--color-text-strong)',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {loginUser.Name || 'User'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          maxWidth: { xs: '100%', md: 1100, lg: 1280 },
          mx: 'auto',
          px: { xs: 1.5, sm: 2.5, md: 3 },
          py: isEmbed ? { xs: 1, sm: 1.25 } : { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {children}
      </Box>

      {!isEmbed && (
        <Fab
          color="primary"
          aria-label="Open announcements"
          onClick={() => setAnnouncementsOpen(true)}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: {
              xs: hasSafeArea ? 'calc(56px + env(safe-area-inset-bottom))' : 56,
              md: hasSafeArea ? 'calc(24px + env(safe-area-inset-bottom))' : 24,
            },
            zIndex: 20,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
          }}
        >
          <CampaignOutlinedIcon />
        </Fab>
      )}

      <ProjectAnnouncementsDialog
        open={announcementsOpen}
        onClose={() => setAnnouncementsOpen(false)}
      />
    </Box>
  );
}
